import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import type { Token } from "../types";
import { useSessionContext } from "../contexts/SessionContext";
import { useCanvasContext } from "../contexts/CanvasContext";
import { useTokenContext } from "../contexts/TokenContext";
import { useSheetContext } from "../contexts/SheetContext";
import { useToolOptions } from "./useToolOptions";
import { useSnapToGrid } from "./useSnapToGrid";
import { useAoeTemplates } from "./useAoeTemplates";
import { useAoePlacement } from "./useAoePlacement";
import { useDrawingSelection } from "./useDrawingSelection";
import { useCanvasScopedQuery } from "../contexts/MapPaneContext";
import type { AvatarSize } from "../components/CharacterPortraits";

/**
 * useSessionCanvasController — the canvas-feeding wiring, extracted from AppShell
 * so BOTH the table (AppShell) and GM Studio (StudioMapCanvas) can drive the same
 * interactive canvas cluster (`<SessionCanvas>`) identically.
 *
 * This is the provider-LESS half of the S4 iter-2 extraction: it owns the hooks
 * and effects that produce the values the cluster consumes (drawingSelection,
 * AoE templates/placement, tool→engine sync, token-upload, per-token description,
 * avatar size). The provider trio (ActorProvider / DrawingSelectionProvider /
 * PageManager) stays with the CALLER — that is the only shape that can also slim
 * AppShell later without nesting, because AppShell's non-cluster consumers
 * (Sidebar → PageOps) need those providers to stay outer.
 *
 * All inputs come from the nearest canvas providers. In GM Studio each map
 * pane mounts its own (StudioMapCanvas, package 5.3), so each pane's
 * controller drives its own engine with its own tool options — two Studio maps
 * never share one engine.
 *
 * NB (dedup debt): AppShell still inlines this same wiring for the live table.
 * When AppShell adopts `<SessionCanvas>` (deferred slim step) its inline copy is
 * deleted in favor of this hook. Kept in sync until then; they never mount
 * together so the duplication is inert, not a live conflict.
 */
export interface SessionCanvasController {
  drawingSelection: ReturnType<typeof useDrawingSelection>;
  clearAllDrawingsRef: React.MutableRefObject<() => void>;
  aoeTemplates: ReturnType<typeof useAoeTemplates>;
  aoePlacement: ReturnType<typeof useAoePlacement>;
  aoeAffectedTokenIds: Map<string, string>;
  handleTokenUpload: () => void;
  getTokenDescription: (token: Token) => string;
  avatarSize: AvatarSize;
  onAvatarSizeChange: (size: AvatarSize) => void;
  linkDrawMode: "rect" | "polygon" | "circle";
}

export function useSessionCanvasController(): SessionCanvasController {
  const { mp, outgoingPageId, pages } = useSessionContext();
  const scopedQuery = useCanvasScopedQuery();
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const {
    engine,
    drawing,
    fog,
    setSnapToGrid,
    setDrawColor,
    setDrawWidth,
  } = useCanvasContext();
  const { notes: notesHook } = useTokenContext();
  const { sheets: charSheets } = useSheetContext();
  const toolOptions = useToolOptions();
  const snapHook = useSnapToGrid();

  // Sync snap (with Alt-override) → canvas refs (BI-001) + draw/fog tool options
  // → engine refs. Mirror of AppShell:157-175.
  useEffect(() => {
    setSnapToGrid(snapHook.snapActive);
  }, [snapHook.snapActive, setSnapToGrid]);
  useEffect(() => {
    setDrawColor(toolOptions.options.drawColor);
  }, [toolOptions.options.drawColor, setDrawColor]);
  useEffect(() => {
    setDrawWidth(toolOptions.options.drawWidth);
  }, [toolOptions.options.drawWidth, setDrawWidth]);
  useEffect(() => {
    fog.fogMode.current = toolOptions.options.fogMode as any;
  }, [toolOptions.options.fogMode, fog]);
  useEffect(() => {
    drawing.shapeType.current = toolOptions.options.shapeType;
  }, [toolOptions.options.shapeType, drawing]);
  useEffect(() => {
    fog.setBrushSize(toolOptions.options.fogBrushSize);
  }, [toolOptions.options.fogBrushSize, fog]);

  const drawingSelection = useDrawingSelection(
    engine,
    drawing,
    () => notesHook.notes,
  );
  const clearAllDrawingsRef = useRef<() => void>(() => {});

  const aoeSend = useCallback(
    (msg: Record<string, unknown>) => {
      if (mp.session) mp.send(msg as any);
    },
    [mp],
  );
  const aoeTemplates = useAoeTemplates(aoeSend, outgoingPageId, engine.markDirty);
  const aoePlacement = useAoePlacement(aoeTemplates);

  // Handle incoming template:* WS messages (AoE). Mirror of AppShell:602-609.
  // Older servers omit pageId and place templates on the players' map.
  useEffect(() => {
    if (!mp.session) return;
    return mp.onMessage((msg: any) => {
      if (msg.type === "template:placed" || msg.type === "template:removed") {
        const p = pagesRef.current;
        if (msg.pageId === undefined && p.viewingPageId !== p.activePageId) return;
        aoeTemplates.handleTemplateMessage(msg);
      }
    });
  }, [mp, aoeTemplates]);

  const aoeAffectedTokenIds = useMemo(() => {
    const ids = new Map<string, string>();
    for (const [templateId, arr] of aoeTemplates.affectedTokens.entries()) {
      const template = aoeTemplates.templates.get(templateId);
      const color = template?.color ?? "rgba(255, 68, 68, 0.3)";
      for (const id of arr) ids.set(id, color);
    }
    return ids;
  }, [aoeTemplates.affectedTokens, aoeTemplates.templates]);

  // Token upload trigger — hidden <input> lives in TokenManager, we click it by
  // selector — within this map pane (Studio can show two). Mirror of
  // AppShell:767-772.
  const handleTokenUpload = useCallback(() => {
    const input = scopedQuery<HTMLInputElement>(
      'input[type="file"][accept="image/*"][hidden]',
    );
    input?.click();
  }, [scopedQuery]);

  // Stabilized per-token description for CanvasArea (React.memo). AppShell:697-708.
  const getTokenDescription = useCallback(
    (t: Token) => {
      if (t.characterSheetId) {
        const sheet = charSheets.sheets.find(
          (s) => s.id === t.characterSheetId,
        );
        if (sheet) return sheet.tokenDescription;
      }
      return t.description;
    },
    [charSheets.sheets],
  );

  const [avatarSize, setAvatarSize] = useState<AvatarSize>(() => {
    try {
      const stored = localStorage.getItem("avatar-size");
      if (stored && ["small", "medium", "large"].includes(stored))
        return stored as AvatarSize;
    } catch {}
    return "medium";
  });
  const onAvatarSizeChange = useCallback((s: AvatarSize) => {
    setAvatarSize(s);
    try {
      localStorage.setItem("avatar-size", s);
    } catch {}
  }, []);

  const linkDrawMode = toolOptions.options.linkDrawMode;

  return {
    drawingSelection,
    clearAllDrawingsRef,
    aoeTemplates,
    aoePlacement,
    aoeAffectedTokenIds,
    handleTokenUpload,
    getTokenDescription,
    avatarSize,
    onAvatarSizeChange,
    linkDrawMode,
  };
}
